import { REG_STATE_COPY } from './eligibility.ts';
import {
  normalizeInstant,
  registrationStateOf,
  sortTournaments,
  storicoEsitoOf,
  tournamentStatusDisplay,
  type LimuloParola,
} from './model.ts';

/**
 * LE QUATTRO TAB DELLE LISTE TORNEI — «In arrivo · In corso · Passati · I miei» (I-346, Luca 2026-10-04 ~13:5xZ: «tab: 1a 2a
 * 3a-b» sulla tavola di I-342, KB `services/qlash-telegram-app/ui/2026-10-04-tornei-quattro-tab.md` @ `70ec31a9`).
 *
 * Le regole stanno QUI e solo qui, pure: le leggono le tre porte (il pannello Eventi della Hub, la lista a tutta pagina, la
 * scheda «Tornei» del backgammon) attraverso `TorneiQuattroTab`.
 *
 * ## La tab pubblica di un torneo — nessuno stato orfano
 * Lo stato pubblico e' uno di sei (`publicStateOf`, `qlash-limulo` `app-qtournaments/app/persistenza/tournament.model.js:191-216`
 * @ `5baeedf`), la finestra d'iscrizione una di cinque (`finestraDi`, `core/iscrizioni.js:257`). Le combinazioni:
 *  - `in_progress`, `paused` ⇒ **In corso**;
 *  - `complete`, `abandoned` (che raccoglie `canceled`), `archived`, e le grafie dello storico ⇒ **Passati** (`storicoEsitoOf`, la
 *    sola enumerazione degli esiti: nessuna seconda lista qui);
 *  - `created` MAI PARTITO — iscrizioni non aperte e `endAt` passato, oppure `startAt` passato da piu' di `ORE_MAI_PARTITO` ⇒
 *    **Passati** (badge «Non ancora iniziato»: e' la parola del motore, nessuno l'ha concluso);
 *  - ogni altro `created` (aperte, annunciate `scheduled`, al completo, chiuse ma non partite) ⇒ **In arrivo**;
 *  - uno `status` che non conosciamo ⇒ **In arrivo**: la stessa regola permissiva di `isPublished`, perche' una lista che nasconde
 *    cio' che non capisce si accorcia da sola.
 */

/**
 * ⚠️ **Una soglia NOSTRA, non un fatto del motore** (tavola I-342 §1.1, passata col default nella scelta I-346). Serve a non
 * mandare nei Passati un torneo `ready` che lo scheduler fa partire con qualche minuto di ritardo; su staging, il 04/10, tre tornei
 * `created` avevano l'inizio passato da giorni.
 */
export const ORE_MAI_PARTITO = 6;

export type TabPubblica = 'arrivo' | 'corso' | 'passati';
export type TabTornei = TabPubblica | 'miei';
/** La tab che si apre: FISSA. Una regola che dipende dai dati cambierebbe la tab sotto il dito a ogni giro di polling. */
export const TAB_TORNEI_INIZIALE: TabTornei = 'arrivo';

const rec = (x: unknown): Record<string, unknown> =>
  x !== null && typeof x === 'object' ? (x as Record<string, unknown>) : {};

/** Un `created` che non partira' piu' da solo: iscrizioni non aperte e il suo tempo e' passato. */
export function maiPartito(tournament: unknown, now: number): boolean {
  if (rec(tournament).status !== 'created') return false;
  if (registrationStateOf(tournament) === 'open') return false;
  const schedule = rec(rec(tournament).schedule);
  const fine = normalizeInstant(schedule.endAt);
  if (fine !== null && fine < now) return true;
  const inizio = normalizeInstant(schedule.startAt);
  return inizio !== null && inizio + ORE_MAI_PARTITO * 3_600_000 < now;
}

export function tabPubblicaDi(tournament: unknown, now: number): TabPubblica {
  const status = rec(tournament).status;
  if (status === 'in_progress' || status === 'paused') return 'corso';
  if (storicoEsitoOf(tournament) !== 'sconosciuto') return 'passati';
  if (maiPartito(tournament, now)) return 'passati';
  return 'arrivo';
}

/**
 * Le origini che stanno SOLO in «I miei» (I-346 1 (a): «le ranked e le sfide 1v1 degli ALTRI escono da TUTTE le liste
 * pubbliche»). Il discriminante e' un DATO scritto alla creazione, `origine` (`ORIGINI_TORNEO`, `tournament.model.js:144`:
 * `manuale` · `coda` · `sit-and-go` · `sfida` · `community`; la `coda` e' la ranked, `:116-121`), esposto dal BFF
 * (`app-qlash-api/backend/policy.js:301`, `null` fuori elenco). ⛔ Mai un'euristica sul nome.
 * ⚠️ `sit-and-go` e `community` RESTANO (sono tornei a cui ci si iscrive); un `null` resta come un `manuale`: l'assenza del
 * campo non diventa il fatto «e' una ranked». `nonEUnaSfida` (`model.ts`) resta la regola della lista per le sfide: questa la
 * estende alle code, per le tab.
 */
export const ORIGINI_SOLO_NEI_MIEI: ReadonlySet<string> = new Set(['coda', 'sfida']);

export function soloNeiMiei(tournament: unknown): boolean {
  const origine = rec(tournament).origine;
  return typeof origine === 'string' && ORIGINI_SOLO_NEI_MIEI.has(origine);
}

/** Il momento con cui si ordinano i passati: la fine, altrimenti l'inizio; senza nessuno dei due, in fondo. */
const quandoPassato = (tournament: unknown): number => {
  const schedule = rec(rec(tournament).schedule);
  return normalizeInstant(schedule.endAt) ?? normalizeInstant(schedule.startAt) ?? Number.NEGATIVE_INFINITY;
};

/**
 * I passati DAL PIU' RECENTE. ⚠️ Non `sortTournaments`: fra i chiusi mette l'inizio crescente, cioe' il piu' vecchio in cima
 * (misurato sulla forma di staging, KB `2026-10-04-tornei-tab-proposte.md` §1.4).
 */
export function ordinaPassati<T>(list: readonly T[]): T[] {
  return list
    .map((t, i) => ({ t, i }))
    .sort((a, b) => quandoPassato(b.t) - quandoPassato(a.t) || a.i - b.i)
    .map((x) => x.t);
}

/**
 * Le origini che restano in «In arrivo» e «In corso» ma NON nello storico pubblico (r13, Luca 05/10: «storico dei sit&go: solo
 * quelli ai quali ho partecipato»). I sit&go degli altri escono da «Passati»; i miei, passati compresi, sono in «I miei», che
 * legge `/mine` (per utente, lo dice il server) e non passa di qui. Stesso meccanismo CLIENT di `soloNeiMiei`: il server non ha
 * un filtro per `origine` sulla vetrina.
 */
export const ORIGINI_SOLO_NEI_MIEI_NEI_PASSATI: ReadonlySet<string> = new Set(['sit-and-go']);

export function soloNeiMieiNeiPassati(tournament: unknown): boolean {
  const origine = rec(tournament).origine;
  return typeof origine === 'string' && ORIGINI_SOLO_NEI_MIEI_NEI_PASSATI.has(origine);
}

/** Le tre liste pubbliche: senza le origini solo-mie (e, nei Passati, senza i sit&go altrui), ognuna nel suo ordine. */
export function listePubbliche<T>(pubblicati: readonly T[], now: number): Record<TabPubblica, T[]> {
  const per: Record<TabPubblica, T[]> = { arrivo: [], corso: [], passati: [] };
  for (const t of pubblicati) {
    if (soloNeiMiei(t)) continue;
    const tab = tabPubblicaDi(t, now);
    if (tab === 'passati' && soloNeiMieiNeiPassati(t)) continue;
    per[tab].push(t);
  }
  return {
    arrivo: sortTournaments<T>(per.arrivo, now),
    corso: sortTournaments<T>(per.corso, now),
    passati: ordinaPassati(per.passati),
  };
}

/** Quanti passati si mostrano alla volta, poi «Carica altri tornei» (I-346, default della tavola §6.11). */
export const PASSATI_PER_VOLTA = 10;

/**
 * «IN GARA» — una riga di `GET /v1/tournaments/mine` in cui sono ancora dentro (I-346, «Tornei ai quali siamo registrati e siamo
 * ancora in gara»).
 *
 * 🔑 **La fonte e' `myRun.status`**, il mio percorso calcolato dal motore (`core/percorso.js` @ `5baeedf`: `withdrawn` se mi sono
 * ritirato; `eliminated` per squalifica, sconfitta decisiva in singola o doppia eliminazione, fuori da una fase successiva gia'
 * partita, o non primo nella classifica finale; `winner`; `in` altrimenti) e passato dal BFF su ogni riga delle mie liste
 * (`withTournamentRun`, `app-qlash-api/backend/tournament-run.js`). Dal vivo c'e' su 18 righe su 18 (consegna-tornei-orcb,
 * 04/10 13:27:11Z).
 * ⛔ **Non `eliminatoDalTorneo`** (`code.ts`): legge gli INCONTRI, cioe' una lettura per ogni torneo aperto, e vale solo in
 * `single-elimination`. `myRun` costa zero letture e copre tutti i formati.
 * ⛔ **Non `party.status` da solo**: ammette `active` · `withdrawn` · `disqualified`, e l'eliminazione non c'e'
 * (`attivita-entita.js:28-31`). E' la base di `iscrizioneAperta` della PR #573 (chiusa), superata da questa.
 *
 * La regola: il torneo non e' passato (la regola di fase qui sopra) **e**:
 *  - **con `myRun`**: `myRun.status === 'in'`. Lo stato del torneo serve: dal vivo 3 dei 4 `in` erano su tornei ANNULLATI. Ed e'
 *    giusta anche a torneo non partito: chi e' iscritto e' `in` (`percorso.js:81-99`);
 *  - **senza `myRun`** (un BFF di prima della 1.91.0 di qTournaments, o uno che un giorno non lo mandasse): la regola di E — la
 *    mia parte non e' USCITA (`party.status` ∉ {`withdrawn`, `disqualified`}; assente = iscritto).
 * 🔴 **RITOCCO di tma-orcb (2026-10-04 14:32:24Z)**: la prima stesura mandava la riga SENZA `myRun` nei Conclusi, cioe'
 * trasformava un dato MANCANTE nell'affermazione «non sono piu' in gara». Se il BFF smettesse di mandarlo, tutte le mie
 * iscrizioni finirebbero nei Conclusi e il bollino sparirebbe. Un dato assente non toglie nessuno dalla gara: decide la parte.
 */
export function inGara(riga: unknown, now: number): boolean {
  if (tabPubblicaDi(riga, now) === 'passati') return false;
  const myRun = rec(riga).myRun;
  if (myRun !== null && typeof myRun === 'object') return rec(myRun).status === 'in';
  const party = rec(rec(riga).party).status;
  return !(party === 'withdrawn' || party === 'disqualified');
}

/**
 * Le mie righe divise in DUE, senza resto: ogni riga sta in uno e un solo sotto-filtro. «Conclusi» e' tutto cio' che non e' «In
 * gara»: torneo passato (qualunque sia `myRun`), eliminato a torneo ancora in corso (I-346 2 (a)), ritirato prima dell'inizio
 * (badge «Ritirato»), riga senza percorso con la parte uscita.
 */
export function divisioneMiei<T>(righe: readonly T[], now: number): { inGara: T[]; conclusi: T[] } {
  const dentro: T[] = [];
  const fuori: T[] = [];
  for (const r of righe) (inGara(r, now) ? dentro : fuori).push(r);
  return { inGara: sortTournaments<T>(dentro, now), conclusi: ordinaPassati(fuori) };
}

/**
 * Il BOLLINO su «I miei»: quanti sono «In gara» — lo stesso numero che si legge sul sotto-filtro, cosi' bollino e contenuto non
 * dicono due cose diverse. Le regole di E (approvate da tma-orcb): `null` — cioe' niente bollino — a zero, finche' `/mine` non ha
 * risposto (`righe` `undefined`), e in errore (chi chiama passa `undefined` quando il dato non e' MAI arrivato).
 */
export function bollinoMiei(righe: readonly unknown[] | undefined, now: number): number | null {
  if (righe === undefined) return null;
  const n = righe.filter((r) => inGara(r, now)).length;
  return n > 0 ? n : null;
}

/**
 * IL BADGE DELLA CARD — la parola nell'angolo, che fino al 04/10 era SEMPRE la finestra d'iscrizione: un torneo concluso, uno
 * annullato, uno in corso e uno che parte fra dieci minuti dicevano tutti «Chiuse» (KB `2026-10-04-tornei-tab-proposte.md` §1.4).
 * Ora dice la cosa che conta per la tab in cui sta la riga, con parole che ESISTONO:
 *  - la MIA parte uscita (solo sulle mie righe, che portano `party`): «Ritirato» / «Squalificato» (`registration.*`);
 *  - In corso e Passati: lo stato del TORNEO (`tournamentStatusDisplay`: «In corso», «In pausa», «Concluso», «Annullato»,
 *    «Archiviato»; un `created` mai partito dice «Non ancora iniziato», la parola del motore);
 *  - In arrivo: la finestra d'iscrizione, come prima («Aperte», «Non ancora aperte», «Al completo», «Chiuse»).
 */
export function badgeDellaCard(tournament: unknown, now: number): LimuloParola {
  const party = rec(rec(tournament).party).status;
  if (party === 'withdrawn') return { i18nKey: 'registration.withdrawn', text: 'withdrawn' };
  if (party === 'disqualified') return { i18nKey: 'registration.disqualified', text: 'disqualified' };
  if (tabPubblicaDi(tournament, now) === 'arrivo') {
    const stato = registrationStateOf(tournament);
    return { i18nKey: REG_STATE_COPY[stato], text: stato };
  }
  return tournamentStatusDisplay(rec(tournament).status);
}
